// MaturaQuadraticSequencePFClosedTask.js
// Zadanie maturalne – zamknięte (P/F):
// Dany wzór na (a_n). Dwa stwierdzenia:
//  1) „Ciąg (a_n) jest arytmetyczny.”
//  2) „Wszystkie wyrazy ciągu (a_n) są liczbami parzystymi.”

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TruthInput from "../../../../components/inputs/TruthInput";

function randInt(min, max) {
  const span = max - min + 1;
  return Math.floor(Math.random() * span) + min;
}

function randNonZeroInt(min, max) {
  let v;
  do {
    v = randInt(min, max);
  } while (v === 0);
  return v;
}

/* =============================== WZORY CIĄGU ===============================

Rodziny, które losujemy:

1) „Kwadratowy” (jak w arkuszu):
   a_n = k n^2 - k n = k n (n - 1)
   → NIE jest arytmetyczny, wszystkie wyrazy PARZYSTE
   → (isArithmetic = false, allEven = true)

2) Liniowy z czynnikiem 2:
   a_n = 2 (p n + q)
   → jest arytmetyczny, wszystkie wyrazy PARZYSTE
   → (true, true)

3) Liniowy bez czynnika 2:
   a_n = (2p+1)n + (2q+1)
   → jest arytmetyczny, ale nie wszystkie wyrazy parzyste
   → (true, false)

======================================================================== */

function buildQuadratic(k) {
  // a_n = k n^2 - k n
  const kLatex = k === 1 ? "" : k === -1 ? "-" : String(k);
  const anLatex = `${kLatex}n^{2} - ${Math.abs(k)}n`;
  return {
    variant: "quadratic",
    aLatex: anLatex,
    isArithmetic: false,
    allEven: true,
    params: { k },
  };
}

function buildLinearEven(p, q) {
  // a_n = 2(p n + q)
  const insideParts = [];
  if (p !== 0) {
    const coeff = p === 1 ? "" : p === -1 ? "-" : String(p);
    insideParts.push(`${coeff}n`);
  }
  if (q !== 0) {
    const sign = q < 0 ? " - " : insideParts.length ? " + " : "";
    const absQ = Math.abs(q);
    insideParts.push(`${sign}${absQ}`);
  }
  const inside = insideParts.join("") || "0";
  const aLatex = `2(${inside})`;

  return {
    variant: "linear_even",
    aLatex,
    isArithmetic: true,
    allEven: true,
    params: { p, q },
  };
}

function buildLinearMixed(p, q) {
  // a_n = (2p+1)n + (2q+1)
  const A = 2 * p + 1;
  const B = 2 * q + 1;

  let first;
  if (A === 1) first = "n";
  else if (A === -1) first = "-n";
  else first = `${A}n`;

  let tail = "";
  if (B !== 0) {
    const sign = B < 0 ? "-" : "+";
    const absB = Math.abs(B);
    tail = ` ${sign} ${absB}`;
  }

  const aLatex = `${first}${tail}`;

  return {
    variant: "linear_mixed",
    aLatex,
    isArithmetic: true,
    allEven: false,
    params: { p, q, A, B },
  };
}

/* =============================== GENERATOR =============================== */

/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 14.2:
 *   a_n = 3n^2 - 3n = 3n(n-1):  arytmetyczny → F,  wszystkie parzyste → P.
 */
function createJune2025OriginalTask() {
  return {
    type: "MaturaQuadraticSequencePFClosedTask",
    data: {
      variant: "quadratic",
      aLatex: "3n^{2} - 3n",
      isArithmetic: false,
      allEven: true,
      params: { k: 3 },
      maxPoints: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: "14.2",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  // losujemy rodzinę: 0 - kwadratowy, 1 - liniowy even, 2 - liniowy mixed
  const family = randInt(0, 2);

  let desc;

  if (family === 0) {
    const k = randNonZeroInt(1, 5);
    desc = buildQuadratic(k);
  } else if (family === 1) {
    let p = randNonZeroInt(-3, 3);
    let q = randInt(-3, 3);
    desc = buildLinearEven(p, q);
  } else {
    const p = randInt(-2, 2);
    const q = randInt(-2, 2);
    desc = buildLinearMixed(p, q);
  }

  return {
    type: "MaturaQuadraticSequencePFClosedTask",
    data: {
      ...desc,
      maxPoints: 1,
    },
  };
}

/* =============================== TREŚĆ ==================================== */

export function renderTask({ data }) {
  const { aLatex } = data;

  return (
    <div style={{ textAlign: "left" }} className="space-y-2">
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Ciąg <InlineMath math={'(a_n)'} /> jest określony wzorem{" "}
        <InlineMath math={`a_n = ${aLatex}`} /> dla każdej liczby naturalnej{" "}
        <InlineMath math={'n \\ge 1'} />.
      </p>
      <p>
        Oceń prawdziwość poniższych stwierdzeń. Wybierz{" "}
        <InlineMath math={'P'} />, jeśli stwierdzenie jest prawdziwe, albo{" "}
        <InlineMath math={'F'} />, jeśli jest fałszywe.
      </p>

      <p>
        <InlineMath math="1." /> Ciąg <InlineMath math={'(a_n)'} /> jest arytmetyczny.
      </p>
      <p>
        <InlineMath math="2." /> Wszystkie wyrazy ciągu <InlineMath math={'(a_n)'} /> są liczbami
        parzystymi.
      </p>
    </div>
  );
}

/* =============================== INPUT P/F ================================ */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};

  const setValue = (key, v) => {
    onChange?.({
      ...value,
      [key]: v,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>1.</span>
        <TruthInput
          value={value.s1 || null}
          disabled={disabled}
          onChange={(v) => setValue("s1", v)}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>2.</span>
        <TruthInput
          value={value.s2 || null}
          disabled={disabled}
          onChange={(v) => setValue("s2", v)}
        />
      </div>
    </div>
  );
}

/* =============================== PODGLĄD ODP. ============================ */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  const s1 = userAnswer.s1 || "-";
  const s2 = userAnswer.s2 || "-";

  const s1Latex = s1 === "-" ? "-" : `\\text{${s1}}`;
  const s2Latex = s2 === "-" ? "-" : `\\text{${s2}}`;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Twoja odpowiedź:</p>
      <p>
        <InlineMath math="1." /> <InlineMath math={s1Latex} />
      </p>
      <p>
        <InlineMath math="2." /> <InlineMath math={s2Latex} />
      </p>
    </div>
  );
}

/* =============================== SPRAWDZANIE ============================= */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const { isArithmetic, allEven } = data;

  const correctS1 = isArithmetic ? "P" : "F";
  const correctS2 = allEven ? "P" : "F";

  const { s1, s2 } = userAnswer;
  if (s1 !== "P" && s1 !== "F") return false;
  if (s2 !== "P" && s2 !== "F") return false;

  // 0–1 punkt: tylko gdy oba trafione
  return s1 === correctS1 && s2 === correctS2;
}

/* =============================== ODP. (P/F) =============================== */

export function renderAnswer({ data }) {
  const { isArithmetic, allEven } = data;

  const s1 = isArithmetic ? "P" : "F";
  const s2 = allEven ? "P" : "F";

  const s1Latex = `\\text{${s1}}`;
  const s2Latex = `\\text{${s2}}`;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Poprawna odpowiedź:</p>
      <p>
        <InlineMath math="1." /> <InlineMath math={s1Latex} />
      </p>
      <p>
        <InlineMath math="2." /> <InlineMath math={s2Latex} />
      </p>
    </div>
  );
}

/* =============================== WYJAŚNIENIE ============================= */

function sequenceValue(variant, params, n) {
  if (variant === "quadratic") return params.k * n * n - params.k * n;
  if (variant === "linear_even") return 2 * (params.p * n + params.q);
  return params.A * n + params.B;
}

function signedTerm(value) {
  if (!value) return "";
  return value > 0 ? `+${value}` : String(value);
}

function substitutedSequenceLatex(variant, params, n) {
  if (variant === "quadratic") {
    return `${params.k}\\cdot ${n}^{2}-${params.k}\\cdot ${n}`;
  }
  if (variant === "linear_even") {
    return `2\\left(${params.p}\\cdot ${n}${signedTerm(params.q)}\\right)`;
  }
  return `${params.A}\\cdot ${n}${signedTerm(params.B)}`;
}

export function renderExplanation({ data }) {
  const { variant, aLatex, params, isArithmetic, allEven } = data;

  const correctS1 = isArithmetic ? "P" : "F";
  const correctS2 = allEven ? "P" : "F";
  const values = [1, 2, 3].map((n) => sequenceValue(variant, params, n));
  const firstDifference = values[1] - values[0];
  const secondDifference = values[2] - values[1];

  return (
    <div style={{ textAlign: "left" }} className="space-y-3">
      <p>
        Mamy ciąg <InlineMath math={'(a_n)'} /> dany wzorem{" "}
        <InlineMath math={`a_n = ${aLatex}`} />.
      </p>

      <div className="space-y-1">
        <p>
          <strong>Krok 1.</strong> Liczymy trzy pierwsze wyrazy ciągu.
        </p>
        {[1, 2, 3].map((n, index) => (
          <BlockMath
            key={n}
            math={`a_${n}=${substitutedSequenceLatex(variant, params, n)}=${values[index]}`}
          />
        ))}
      </div>

      <div className="space-y-1">
        <p>
          <strong>Krok 2.</strong> Sprawdzamy, czy różnice między kolejnymi
          wyrazami są jednakowe.
        </p>
        <BlockMath
          math={`a_2-a_1=${firstDifference},\\qquad a_3-a_2=${secondDifference}`}
        />
        {isArithmetic ? (
          <p>
            Różnice są jednakowe. Tak samo będzie dla kolejnych wyrazów,
            ponieważ we wzorze <InlineMath math={`a_n=${aLatex}`} /> liczba{" "}
            <InlineMath math={'n'} /> występuje tylko w pierwszej potędze.
            Ciąg jest więc arytmetyczny — odpowiedź{" "}
            <InlineMath math={'P'} />.
          </p>
        ) : (
          <p>
            Już te dwie różnice są różne, więc ciąg nie jest arytmetyczny —
            odpowiedź <InlineMath math={'F'} />.
          </p>
        )}
      </div>

      <div className="space-y-1">
        <p>
          <strong>Krok 3.</strong> Sprawdzamy parzystość wyrazów. Pierwsze
          trzy wyrazy pozwalają zauważyć prawidłowość.
        </p>
        {variant === "quadratic" && (
          <>
            <BlockMath
              math={
                params.k === 1
                  ? "a_n = n(n-1)"
                  : `a_n = ${params.k}n(n-1)`
              }
            />
            <p>
              Dla pewności uzasadniamy ją krótko: liczby{" "}
              <InlineMath math={'n'} /> i{" "}
              <InlineMath math={'n-1'} /> są kolejnymi liczbami naturalnymi,
              więc jedna z nich jest parzysta. Iloczyn{" "}
              <InlineMath math={'n(n-1)'} /> jest parzysty, a po
              pomnożeniu przez całkowitą liczbę{" "}
              <InlineMath math={String(params.k)} /> nadal otrzymujemy liczbę
              parzystą. Zatem wszystkie wyrazy są parzyste – poprawną
              odpowiedzią jest <InlineMath math={'P'} />.
            </p>
          </>
        )}

        {variant === "linear_even" && (
          <>
            <p>
              Zauważoną prawidłowość potwierdza czynnik <InlineMath math="2" /> we wzorze. Każdy
              wyraz ma postać{" "}
              <InlineMath math={'a_n = 2 \\cdot (\\text{liczba całkowita})'} />,
              więc jest liczbą parzystą. W drugim wierszu poprawną odpowiedzią
              jest <InlineMath math={'P'} />.
            </p>
          </>
        )}

        {variant === "linear_mixed" && (
          <p>
            Wśród obliczonych wyrazów znajduje się liczba nieparzysta. To
            wystarcza, aby stwierdzenie „wszystkie wyrazy są parzyste” uznać
            za fałszywe — odpowiedź <InlineMath math={'F'} />.
          </p>
        )}
      </div>

      <p>
        Podsumowując: poprawne odpowiedzi to{" "}
        <InlineMath math={`1.\\ \\text{${correctS1}}`} /> oraz{" "}
        <InlineMath math={`2.\\ \\text{${correctS2}}`} />.
      </p>
    </div>
  );
}

/* =============================== EXPORT DOMYŚLNY ========================= */

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
